import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Select, Space, Table, Tag, Typography } from 'antd';
import {
  listSalesTeamTransferRegister,
  listSalesTeams,
  type SalesTeamTransferRow,
} from '@/services/salesTeam.service';
import { SALES_TEAM_ENTITY_TYPES } from '@/constants/salesTeamEntityTypes';

const { Title } = Typography;

const SalesTeamTransferRegisterPage: React.FC = () => {
  const [entityType, setEntityType] = useState<string | undefined>();
  const [teamId, setTeamId] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const { data: teams = [] } = useQuery({
    queryKey: ['sales-teams'],
    queryFn: () => listSalesTeams(true),
  });

  const { data: register, isLoading } = useQuery({
    queryKey: ['sales-team-transfer-register', entityType, teamId, page],
    queryFn: () =>
      listSalesTeamTransferRegister({
        entity_type: entityType,
        team_id: teamId,
        page,
        page_size: pageSize,
      }),
  });

  const entityOptions = useMemo(
    () => SALES_TEAM_ENTITY_TYPES.map((t) => ({ value: t, label: t })),
    []
  );

  const teamOptions = useMemo(
    () => teams.map((t) => ({ value: t.id, label: t.team_name })),
    [teams]
  );

  const columns = [
    {
      title: 'Date',
      dataIndex: 'transfer_date',
      key: 'transfer_date',
      width: 170,
      render: (v: string) => (v ? new Date(v).toLocaleString() : '—'),
    },
    { title: 'Entity', dataIndex: 'entity_type', key: 'entity_type', width: 140 },
    {
      title: 'Document',
      key: 'document',
      render: (_: unknown, r: SalesTeamTransferRow) => r.document_number || r.entity_id,
    },
    {
      title: 'From team',
      key: 'from_team',
      render: (_: unknown, r: SalesTeamTransferRow) => r.from_team_name || '—',
    },
    {
      title: 'To team',
      key: 'to_team',
      render: (_: unknown, r: SalesTeamTransferRow) => r.to_team_name || '—',
    },
    { title: 'Reason', dataIndex: 'reason', key: 'reason', ellipsis: true },
    {
      title: 'Downstream',
      dataIndex: 'transfer_downstream',
      key: 'transfer_downstream',
      width: 90,
      render: (v: boolean) => (v ? <Tag color="blue">Yes</Tag> : <Tag>No</Tag>),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Sales team transfer register</Title>
      <Card>
        <Space wrap style={{ marginBottom: 16 }}>
          <Select
            allowClear
            placeholder="Entity type"
            style={{ width: 200 }}
            options={entityOptions}
            value={entityType}
            onChange={(v) => {
              setEntityType(v);
              setPage(1);
            }}
          />
          <Select
            allowClear
            placeholder="Team"
            style={{ width: 220 }}
            options={teamOptions}
            value={teamId}
            onChange={(v) => {
              setTeamId(v);
              setPage(1);
            }}
          />
        </Space>
        <Table
          rowKey="id"
          loading={isLoading}
          dataSource={register?.data ?? []}
          columns={columns}
          pagination={{
            current: page,
            pageSize,
            total: register?.total ?? 0,
            onChange: setPage,
          }}
        />
      </Card>
    </div>
  );
};

export default SalesTeamTransferRegisterPage;
